import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import {
  Plane, Building2, Car, Package, MapPin, Ticket, Plus, Ban, ArrowRight,
  Clock, CheckCircle2, XCircle,
} from "lucide-react";

const ICONS = { flight: Plane, hotel: Building2, car: Car, package: Package, sightseeing: MapPin };

export default function MyBookings() {
  const [items, setItems] = useState([]);
  const { user, loading } = useAuth();
  const { format: fmt } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    (async () => { try { const { data } = await api.get("/bookings/mine"); setItems(data); } catch (_e) {} })();
  }, [user, loading, navigate]);

  const statusBadge = (s) => {
    const map = {
      confirmed: "bg-emerald-100 text-emerald-700",
      pending_payment: "bg-amber-100 text-amber-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return <Badge className={`${map[s] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>{s.replace("_", " ")}</Badge>;
  };

  const cancelReqBadge = (s) => {
    if (!s) return null;
    const map = {
      pending: { c: "bg-amber-100 text-amber-700", Icon: Clock },
      approved: { c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
      rejected: { c: "bg-red-100 text-red-700", Icon: XCircle },
    }[s] || { c: "bg-slate-100 text-slate-700", Icon: Clock };
    const Icon = map.Icon;
    return <Badge className={`${map.c} border-0 capitalize`}><Icon className="w-3 h-3 mr-1" /> Cancel {s}</Badge>;
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">My bookings</h1>
      <p className="text-slate-500 mt-1">All your trips, neatly in one place.</p>

      {items.length === 0 && (
        <div className="mt-10 text-center text-slate-500 py-12 border border-dashed rounded-2xl">
          No bookings yet. Start exploring!
        </div>
      )}

      <div className="mt-6 space-y-4">
        {items.map((b) => {
          const Icon = ICONS[b.booking_type] || Plane;
          const isFlight = b.booking_type === "flight";
          const isConfirmed = b.status === "confirmed";
          const cancelPending = b.cancel_request_status === "pending";
          const canRequestCancel = b.status !== "cancelled" && !cancelPending;
          return (
            <Card key={b.id} data-testid={`booking-row-${b.id}`} className="p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center text-[var(--brand-primary)]">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-[var(--brand-accent)] capitalize">{b.booking_type} booking</div>
                    <div className="text-xs text-slate-500">
                      {(() => {
                        const supplierPnr = b.travelport_locator || b.amadeus_locator || "";
                        const ticketNo = (b.travelport_tickets?.[0]?.number)
                          || (b.amadeus_tickets?.[0]?.number) || "";
                        return (
                          <>
                            {supplierPnr ? <>Airline PNR: <span className="font-mono">{supplierPnr}</span></> : <>PNR: <span className="font-mono">{b.pnr}</span></>}
                            {ticketNo && <> · Ticket: <span className="font-mono">{ticketNo}</span></>}
                            <> · {new Date(b.created_at).toLocaleString()}</>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {statusBadge(b.status)}
                  {cancelReqBadge(b.cancel_request_status)}
                  <div className="font-heading text-xl font-bold text-[var(--brand-accent)]">{fmt(Number(b.total_amount))}</div>
                </div>
              </div>
              <div className="text-sm text-slate-600 mt-3 truncate">
                {isFlight && b.item?.segments?.length
                  ? b.item.segments.map((s) => `${s.from}→${s.to} (${s.airline_code} ${s.flight_number})`).join(" · ")
                  : (b.item?.title || b.item?.name || b.item?.model || "—")}
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                {isConfirmed && (
                  <Button data-testid={`booking-view-${b.id}`} variant="outline"
                          className="rounded-full" onClick={() => navigate(`/bookings/${b.id}`)}>
                    <Ticket className="w-4 h-4 mr-2" /> View ticket
                  </Button>
                )}
                {!isConfirmed && b.status !== "cancelled" && (
                  <>
                    <Button data-testid={`booking-pay-${b.id}`}
                            onClick={() => navigate(`/checkout/${b.id}`)}
                            className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
                      Complete payment <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <Button data-testid={`booking-view-${b.id}`} variant="outline" className="rounded-full"
                            onClick={() => navigate(`/bookings/${b.id}`)}>
                      View details
                    </Button>
                  </>
                )}
                {isFlight && isConfirmed && (
                  <Button data-testid={`booking-addons-${b.id}`}
                          onClick={() => navigate(`/bookings/${b.id}/addons`)}
                          className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
                    <Plus className="w-4 h-4 mr-2" /> Add seats / bags / meals
                  </Button>
                )}
                {canRequestCancel && (
                  <Button data-testid={`booking-cancel-${b.id}`} variant="outline"
                          onClick={() => navigate(`/bookings/${b.id}`)}
                          className="rounded-full text-red-600 hover:bg-red-50 border-red-200">
                    <Ban className="w-4 h-4 mr-2" /> Request cancellation
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
